import React from 'react';
import { Link, NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useShopAdminAuth } from '../../context/ShopAdminAuthContext';
import { Button } from '../../components/ui/button';
import { NotificationBell } from '../../components/NotificationBell';
import {
  LayoutDashboard, ShoppingBag, Package, Tag, Truck, Users, LogOut, Store, CreditCard, BarChart3, Settings, Bell, Barcode, Sheet,
} from 'lucide-react';

const LOGO = '/bison-logo.png';

const MENU = [
  { to: '/shop-admin', icon: LayoutDashboard, label: 'Dashboard', end: true, hideForRoles: ['cashier', 'supervisor', 'rider'] },
  { to: '/shop-admin/pos', icon: CreditCard, label: 'POS', perm: 'process_pos_sale' },
  { to: '/shop-admin/orders', icon: ShoppingBag, label: 'Orders', perm: 'view_shop_orders' },
  { to: '/shop-admin/products', icon: Package, label: 'Products', perm: 'manage_shop_products' },
  { to: '/shop-admin/products/bulk-edit', icon: Sheet, label: 'Bulk stock & prices', perm: 'manage_shop_products' },
  { to: '/shop-admin/barcode-labels', icon: Barcode, label: 'Barcode Labels', perm: 'manage_shop_products' },
  { to: '/shop-admin/customers', icon: BarChart3, label: 'Customers', perm: 'view_shop_analytics' },
  { to: '/shop-admin/promos', icon: Tag, label: 'Promo Codes', perm: 'manage_shop_promos' },
  { to: '/shop-admin/rider', icon: Truck, label: 'Rider', perm: 'manage_deliveries' },
  { to: '/shop-admin/delivery-settings', icon: Settings, label: 'Delivery Settings', perm: 'manage_shop_orders' },
  { to: '/shop-admin/users', icon: Users, label: 'Staff', perm: 'manage_shop_staff' },
  { to: '/shop-admin/notifications', icon: Bell, label: 'Notifications', perm: 'manage_shop_staff' },
];

export const ShopAdminLayout = () => {
  const { staff, token, loading, logout, hasPerm } = useShopAdminAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!staff) return <Navigate to="/shop-admin/login" replace />;

  const customerHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <div className="min-h-screen flex bg-muted/20">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-border flex-shrink-0 hidden md:flex flex-col">
        <Link to="/shop-admin" className="p-5 border-b border-border flex items-center gap-2" data-testid="shop-admin-logo">
          <img src={LOGO} alt="Bison" className="h-8 w-auto" />
          <div>
            <div className="font-serif font-semibold leading-tight">Bison Shop</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Back-office</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {MENU.map((m) => {
            if (m.perm && !hasPerm(m.perm)) return null;
            if (m.hideForRoles && m.hideForRoles.includes(staff.role)) return null;
            return (
              <NavLink
                key={m.to}
                to={m.to}
                end={m.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                    isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/60'
                  }`
                }
                data-testid={`shop-admin-nav-${m.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <m.icon className="w-4 h-4" /> {m.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <a href="/shop" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/60" data-testid="shop-admin-view-storefront">
            <Store className="w-4 h-4" /> View storefront
          </a>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <img src={LOGO} alt="Bison" className="h-7 w-auto" />
            <span className="font-serif font-semibold">Shop Admin</span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <NotificationBell audience="staff" headers={customerHeaders} linkPrefix="/shop-admin/orders" />
            <div className="text-sm">
              <div className="font-medium leading-tight">{staff.name}</div>
              <div className="text-xs text-muted-foreground capitalize leading-tight">{staff.role}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/shop-admin/login'); }} data-testid="shop-admin-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
